<soft-page>
  <soft-page-header>
    <div>
      <p class="soft-title">{{ '#SALES.CONFIG.TITLE' | translate }}</p>
      <p class="text-sm text-light">{{ '#SALES.CONFIG.SUBTITLE' | translate }}</p>
    </div>
  </soft-page-header>

  <div class="flex justify-center mt-3">
    <div class="w-full max-w-4xl flex flex-col gap-4">
      <div class="flex gap-4">
        <div class="flex-1">
          <span class="soft-label top block mb-2">
            {{ '#SALES.CONFIG.DASHBOARD_MONTHS_BACK' | translate }}
          </span>
          <soft-number-input
            [value]="monthsBack()"
            (valueChange)="onMonthsChange($event)"
            data-testid="dashboard-months-back-input"
          />
        </div>
        <div class="flex-1">
          <span class="soft-label top block mb-2">
            {{ '#SALES.CONFIG.DATE_FROM' | translate }}
          </span>
          <soft-date-input
            [value]="dateFrom()"
            (valueChange)="onDateFromChange($event)"
            data-testid="date-from-input"
          />
        </div>
      </div>
      <div class="flex gap-4">
        <div class="flex-1">
          <span class="soft-label top block mb-2">
            {{ '#SALES.CONFIG.DEFAULT_DOCUMENT_TYPE' | translate }}
          </span>
          <soft-select
            [formField]="configForm.defaultDocumentType"
            [placeholder]="('#SALES.CONFIG.NO_DEFAULT' | translate) ?? null"
            data-testid="default-document-type-select"
          >
            @for (opt of validDocTypeOptions(); track opt.type) {
              <soft-select-option [value]="opt.type">
                {{ opt.displayName }}
              </soft-select-option>
            }
          </soft-select>
        </div>
        <div class="flex-1">
          <span class="soft-label top block mb-2">
            {{ '#SALES.CONFIG.DEFAULT_DISTRIBUTION_TYPE' | translate }}
          </span>
          <soft-select
            [formField]="configForm.defaultDistributionType"
            [placeholder]="('#SALES.CONFIG.NO_DEFAULT' | translate) ?? null"
            data-testid="default-distribution-type-select"
          >
            @for (dt of distributionTypes(); track dt.id) {
              <soft-select-option [value]="dt.id">
                {{ dt.name }}
              </soft-select-option>
            }
          </soft-select>
        </div>
      </div>
      <div>
        <span class="soft-label top block mb-2">
          {{ '#SALES.CONFIG.VALID_DOCUMENT_TYPES' | translate }}
        </span>
        <div
          cdkDropList
          (cdkDropListDropped)="onDocTypeDrop($event)"
          class="flex flex-col gap-1"
          data-testid="valid-document-types-checkboxes"
        >
          @for (opt of orderedDocTypeOptions(); track opt.type) {
            <div cdkDrag class="flex flex-col">
              <div
                *cdkDragPlaceholder
                class="rounded px-2 py-1 border-2 border-dashed border-primary/30 bg-primary/5 h-8"
              ></div>
              <div
                class="flex items-center gap-2 rounded px-2 py-1 bg-surface
                       hover:bg-hover cursor-grab active:cursor-grabbing"
              >
                <soft-icon
                  name="fa-regular fa-grip-vertical"
                  class="text-light text-xs shrink-0"
                />
                <label class="cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    class="soft-checkbox"
                    [checked]="isDocTypeSelected(opt.type)"
                    (change)="toggleDocType(opt.type)"
                  />
                </label>
                <span class="flex-1">{{ opt.displayName }}</span>
                @if (data().defaultDocumentType === opt.type) {
                  <span class="text-xs text-primary font-semibold shrink-0">
                    Standard
                  </span>
                }
                @if (isDocTypeSelected(opt.type)) {
                  <button
                    type="button"
                    class="text-xs text-light hover:text-primary shrink-0 px-2 py-0.5 cursor-pointer"
                    (click)="toggleAgingEditor(opt.type)"
                  >
                    @let bc = bucketsFor(opt.type).length;
                    {{ '#SALES.CONFIG.AGING_BUCKETS' | translate }}
                    @if (hasOverride(opt.type)) {
                      ({{ bc }})
                    } @else {
                      <span class="text-xs text-light">
                        ({{ '#SALES.CONFIG.AGING_DEFAULT' | translate }})
                      </span>
                    }
                    <i
                      class="fa-regular fa-chevron-down ml-1 transition-transform"
                      [class.rotate-180]="expandedAgingType() === opt.type"
                      aria-hidden="true"
                    ></i>
                  </button>
                }
              </div>
              @if (
                isDocTypeSelected(opt.type) && expandedAgingType() === opt.type
              ) {
                <div class="ml-8 mt-1 mb-2 p-3 rounded border bg-surface-muted flex flex-col gap-3">
                  @if (!hasOverride(opt.type)) {
                    <div class="flex items-center justify-between text-xs text-light">
                      <span>
                        {{ '#SALES.CONFIG.AGING_USING_DEFAULTS' | translate }}
                      </span>
                      <button
                        type="button"
                        class="text-sm text-primary hover:underline cursor-pointer"
                        (click)="initFromDefaults(opt.type)"
                      >
                        {{ '#SALES.CONFIG.AGING_CUSTOMIZE' | translate }}
                      </button>
                    </div>
                    <div class="flex flex-col gap-1.5">
                      @for (b of effectiveBucketsFor(opt.type); track $index) {
                        <div class="flex items-center gap-2 text-sm">
                          <span
                            class="inline-block w-3 h-3 rounded-full shrink-0"
                            [style.background]="b.color"
                          ></span>
                          <span class="flex-1">{{ b.label }}</span>
                          <span class="text-light tabular-nums">
                            {{ b.fromDays }} –
                            {{
                              b.toDays !== null
                                ? b.toDays
                                : ('#SALES.CONFIG.AGING_TO_DAYS_OPEN' | translate)
                            }}
                          </span>
                        </div>
                      }
                    </div>
                  } @else {
                  <div class="grid grid-cols-[2rem_minmax(0,1fr)_5rem_5rem_2rem] gap-2 items-center text-xs text-light px-1">
                    <span></span>
                    <span>{{ '#SALES.CONFIG.AGING_LABEL' | translate }}</span>
                    <span>{{ '#SALES.CONFIG.AGING_FROM_DAYS' | translate }}</span>
                    <span>{{ '#SALES.CONFIG.AGING_TO_DAYS' | translate }}</span>
                    <span></span>
                  </div>
                  @for (b of bucketsFor(opt.type); track $index) {
                    <div class="grid grid-cols-[2rem_minmax(0,1fr)_5rem_5rem_2rem] gap-2 items-center">
                      <input
                        type="color"
                        class="w-8 h-8 rounded border cursor-pointer p-0"
                        [value]="b.color"
                        (input)="
                          updateBucket(opt.type, $index, {
                            color: $any($event.target).value
                          })
                        "
                      />
                      <soft-input
                        [value]="b.label"
                        [placeholder]="
                          ('#SALES.CONFIG.AGING_LABEL' | translate) ?? ''
                        "
                        (valueChange)="
                          updateBucket(opt.type, $index, { label: $any($event) })
                        "
                      />
                      <soft-number-input
                        [value]="b.fromDays"
                        (valueChange)="
                          updateBucket(opt.type, $index, {
                            fromDays: $any($event) ?? 0
                          })
                        "
                      />
                      <soft-number-input
                        [value]="b.toDays"
                        [placeholder]="
                          ('#SALES.CONFIG.AGING_TO_DAYS_OPEN' | translate) ?? ''
                        "
                        (valueChange)="
                          onBucketToDaysChange(opt.type, $index, $any($event))
                        "
                      />
                      <button
                        type="button"
                        class="text-light hover:text-danger cursor-pointer flex items-center justify-center w-8 h-8"
                        [attr.aria-label]="
                          '#SALES.CONFIG.AGING_REMOVE' | translate
                        "
                        (click)="removeBucket(opt.type, $index)"
                      >
                        <i class="fa-regular fa-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  }
                  <div class="flex items-center justify-between">
                    <button
                      type="button"
                      class="text-sm text-primary hover:underline cursor-pointer"
                      (click)="addBucket(opt.type)"
                    >
                      <i
                        class="fa-regular fa-plus mr-1"
                        aria-hidden="true"
                      ></i>
                      {{ '#SALES.CONFIG.AGING_ADD' | translate }}
                    </button>
                    <button
                      type="button"
                      class="text-sm text-light hover:text-primary cursor-pointer"
                      (click)="resetToDefaults(opt.type)"
                    >
                      {{ '#SALES.CONFIG.AGING_RESET' | translate }}
                    </button>
                  </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>
</soft-page>
